import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Grid, Icon, useTheme } from '@wegroup/design-system';

// Components
import ComparisonMatrixHeader from './header/ComparisonMatrixHeader';
import ComparisonMatrixOptions from './options/ComparisonMatrixOptions';
import ComparisonMatrixOverview from './overview/ComparisonMatrixOverview';

// Types
import {
  QuoteQuality,
  LouiseQuote,
  isLouiseQuoteWithQuality,
  LouiseQuoteWithQuality,
} from '../../../../../../../../types/Quotes';

interface Props {
  riskObject: string;
  quotes: LouiseQuote[];
  options: (QuoteQuality | undefined)[];
  isComparisonActive: boolean;
  setIsComparisonActive: (arg0: boolean) => void;
  addToSelectedOffers: (arg0: string) => void;
  removeFromSelectedOffers: (arg0: string) => void;
}

const DesktopComparisonMatrix: React.FC<React.PropsWithChildren<Props>> = ({
  riskObject,
  quotes,
  options,
  setIsComparisonActive,
  isComparisonActive,
  addToSelectedOffers,
  removeFromSelectedOffers,
}) => {
  const { t } = useTranslation();
  const { sizes } = useTheme();

  return (
    <Box
      overflowX="auto"
      padding="9.6rem 4.8rem 4.8rem"
      h="100%"
      position="fixed"
      left="0"
      zIndex="10"
      bg="white"
      w="100%"
      top={isComparisonActive ? '0' : '100%'}
      transition="0.3s"
    >
      <Grid
        w="fit-content"
        margin="0 auto"
        gridTemplateColumns={{
          base: `${sizes[80]} repeat(${quotes.length}, ${sizes[56]}) ${sizes[36]}`,
          laptopL: `${sizes[96]} repeat(${quotes.length}, ${sizes[56]}) ${sizes[36]}`,
          desktopS: `${sizes['md']} repeat(${quotes.length}, ${sizes[56]}) ${sizes[36]}`,
        }}
      >
        <Button
          borderRadius="full"
          top="6"
          pos="absolute"
          variant="tertiary"
          onClick={() => setIsComparisonActive(false)}
          leftIcon={<Icon name="GeneralActionsChevronLeftRounded" />}
        >
          {t('Back to offers')}
        </Button>
        <ComparisonMatrixHeader
          riskObject={riskObject}
          quotes={quotes}
          addToSelectedOffers={addToSelectedOffers}
          removeFromSelectedOffers={removeFromSelectedOffers}
        />
        <ComparisonMatrixOverview quotes={quotes} />
        {quotes.every((quote) => isLouiseQuoteWithQuality(quote)) &&
          options.map((quality, index) => {
            const typedQuality = quality as QuoteQuality;
            return (
              <ComparisonMatrixOptions
                key={`comparison_options_${typedQuality.key}`}
                // Check is done above, TS does not see it
                quotes={quotes as LouiseQuoteWithQuality[]}
                option={typedQuality}
                last={index === options.length - 1}
              />
            );
          })}
      </Grid>
    </Box>
  );
};

export default DesktopComparisonMatrix;
